import { Center, Loader } from '@mantine/core';
import { plural, sinceLabel } from '../lib/ui';
import { useIngestStatus, type IngestFolder } from '../api/mail';

/**
 * Nabiralnik — is inbound mail actually arriving?
 *
 * The poll runs against Zoho every minute and, when it breaks, breaks quietly:
 * the only symptom is mail that does not arrive, which looks like a quiet week.
 * So the screen leads with the one sentence that matters — reading, or not —
 * and puts the numbers behind it, rather than making a coordinator assemble the
 * verdict out of timestamps.
 */
export function IngestStatus() {
  const status = useIngestStatus();

  if (status.isPending) {
    return (
      <Center mih="40vh">
        <Loader />
      </Center>
    );
  }
  // A screen built to end silent failure must not fail silently itself:
  // rendering nothing here would read as "nothing to report" rather than
  // "we cannot tell you".
  if (status.isError || !status.data) {
    return (
      <div className="ml-inbox">
        <Banner tone="bad" headline="Stanja nabiralnika ni bilo mogoče prebrati">
          Aplikacija ni dobila odgovora od strežnika, zato o branju pošte ne more povedati
          ničesar — ne da deluje in ne da ne. Poskusite osvežiti stran.
        </Banner>
      </div>
    );
  }
  const { configured, folders, triage } = status.data;

  return (
    <div className="ml-inbox">
      <Verdict configured={configured} folders={folders} />

      {folders.length > 0 && (
        <div className="ml-folders">
          {folders.map((f) => (
            <FolderCard key={f.path} folder={f} />
          ))}
        </div>
      )}

      <div className="ml-inbox-note">
        <div className="ml-eyebrow">NERAZVRŠČENO</div>
        <p className="ml-hint">
          {triage === 0
            ? 'Nič ne čaka na razvrstitev.'
            : `${triage} ${plural(triage, 'sporočilo', 'sporočili', 'sporočila', 'sporočil')} s neznanega naslova ${plural(triage, 'čaka', 'čakata', 'čakajo', 'čaka')}, da ${plural(triage, 'ga', 'ju', 'jih', 'jih')} pripnete osebi. Najdete ${plural(triage, 'ga', 'ju', 'jih', 'jih')} pod Prostovoljci → Nabiralnik.`}
        </p>
      </div>

      <div className="ml-inbox-note">
        <div className="ml-eyebrow">KAKO DELUJE</div>
        <p className="ml-hint">
          Predal je vaš: aplikacija ga samo bere, nikoli ne označi sporočila kot prebrano in
          nič ne premakne. Prebrano ali neprebrano ne vpliva na to, kaj se prepiše v nit — če
          torej v Zohu sporočilo odprete prej kot aplikacija, se vseeno prepiše. Kar odgovorite
          iz predala, se prebere iz mape Poslano in pripne v isto nit.
        </p>
        <p className="ml-hint">
          Bere pa samo mapi <strong>Prejeto</strong> in <strong>Poslano</strong>. Če sporočilo
          preložite v svojo mapo, preden ga aplikacija prebere, ne pride v nobeno nit —
          počakajte, da se pojavi v niti, in ga šele nato pospravite.
        </p>
      </div>
    </div>
  );
}

/** The one sentence, and the reason under it when there is one. */
function Verdict({ configured, folders }: { configured: boolean; folders: IngestFolder[] }) {
  if (!configured) {
    return (
      <Banner tone="off" headline="Branje pošte ni nastavljeno">
        Na tem okolju ni podatkov za dostop do predala, zato aplikacija pošte ne bere. To ni
        napaka.
      </Banner>
    );
  }
  if (folders.length === 0) {
    return (
      <Banner tone="warn" headline="Branja še ni bilo">
        Od zadnjega zagona aplikacija predala še ni prebrala. Če to ne izgine v nekaj minutah,
        nekaj ni v redu.
      </Banner>
    );
  }

  const broken = folders.filter((f) => f.failures > 0);
  if (broken.length > 0) {
    const worst = broken.reduce((a, b) => (b.failures > a.failures ? b : a));
    return (
      <Banner
        tone="bad"
        headline={`Branje pošte ne deluje — ${worst.failures} ${plural(worst.failures, 'neuspel poskus', 'neuspela poskusa', 'neuspeli poskusi', 'neuspelih poskusov')} zapored`}
      >
        Mapa <code>{worst.path}</code> je bila nazadnje uspešno prebrana{' '}
        {sinceLabel(worst.lastOkAt) ?? 'nikoli'}. Zadnja napaka:{' '}
        <code>{worst.lastError || 'neznana'}</code>
      </Banner>
    );
  }

  const oldest = folders.reduce((a, b) => (stamp(b.lastOkAt) < stamp(a.lastOkAt) ? b : a));
  const stale = minutesSince(oldest.lastOkAt) > 10;
  return (
    <Banner
      tone={stale ? 'warn' : 'ok'}
      headline={stale ? 'Branje pošte zamuja' : 'Branje pošte deluje'}
    >
      Nazadnje prebrano {sinceLabel(oldest.lastOkAt) ?? 'nikoli'}
      {stale ? ' — pričakovano je vsako minuto.' : '.'}
    </Banner>
  );
}

function FolderCard({ folder }: { folder: IngestFolder }) {
  const arriving = folder.direction === 'in';
  return (
    <div className="ml-folder">
      <div className="ml-folder-head">
        <span className="ml-folder-path">{folder.path}</span>
        <span className="ml-folder-dir">{arriving ? 'prejeto' : 'poslano'}</span>
      </div>
      {/*
        Two counts, never one subtracted from the other: "v predalu" is what
        the folder holds right now, which the coordinator is invited to keep
        small, and "v nitih" is every mail ever read out of it. A season in,
        twelve against four thousand is health, not a gap — so the numbers are
        shown as what they are and neither is presented as an accounting of the
        other.
      */}
      <dl className="ml-folder-facts">
        <Fact label="zdaj v predalu" value={folder.mailCount} />
        <Fact label={arriving ? 'doslej v niti' : 'doslej v niti (prostovoljcem)'} value={folder.heldCount} />
        <Fact label="zadnjič prebrano" value={folder.lastIngested} />
        <Fact label="nazadnje" value={sinceLabel(folder.lastOkAt) ?? 'nikoli'} />
      </dl>
      {!arriving && (
        <p className="ml-hint">
          Pošta iz te mape se pripne le, če je naslovljena na prostovoljca — vaše dopisovanje z
          dobavitelji in drugimi ostane zunaj niti.
        </p>
      )}
    </div>
  );
}

function Fact({ label, value }: { label: string; value: string | number }) {
  return (
    <div className="ml-fact">
      <dt>{label}</dt>
      <dd>{value}</dd>
    </div>
  );
}

function Banner({
  tone,
  headline,
  children,
}: {
  tone: 'ok' | 'warn' | 'bad' | 'off';
  headline: string;
  children: React.ReactNode;
}) {
  return (
    <div className={`ml-banner ml-banner--${tone}`}>
      <div className="ml-banner-head">{headline}</div>
      <div className="ml-banner-body">{children}</div>
    </div>
  );
}

const stamp = (iso: string | null) => (iso ? new Date(iso).getTime() : 0);
const minutesSince = (iso: string | null) =>
  iso ? (Date.now() - new Date(iso).getTime()) / 60_000 : Infinity;
